/*
 * Globo Product Options price reconciler (formerly: decimal trim).
 *
 * TWO JOBS:
 *
 * 1. FORMAT — the theme renders every price through `money_without_trailing_zeros`,
 *    so whole-dollar amounts drop ".00" while ".25"/".50" survive. Globo writes the
 *    PDP price into [data-gpo-product-unit-price] using the raw store money format
 *    (which includes ".00"), producing e.g. "$208.00" next to a compare-at of "$245".
 *    We strip a trailing zero-cents group to match the rest of the theme.
 *
 * 2. CORRECTNESS — Globo's variant-change handler writes the PREVIOUS variant's
 *    price, and only Globo's ~1s polling setInterval (window.gpoInterval) later
 *    heals it. That makes a correct price depend on a timer firing. In the theme
 *    editor the preview is a cross-origin, frequently-occluded iframe, and Chrome
 *    heavily throttles timers in background/hidden frames — so the healer stalls
 *    and the stale price persists. That is the "Rx PDP shows $395 while Single
 *    Vision is selected" bug: intermittent, always exactly one step behind, and
 *    self-healing if you wait.
 *
 *    Proven 2026-08-08 by clearing window.gpoInterval and toggling: every theme
 *    signal (cart form input[name=id], variant-picker JSON, checked radio) was
 *    correct while the header alone showed the old price in Globo's ".00" format.
 *    With the interval alive the value is correct from ~380ms. So the interval is
 *    the healer, not the villain — the defect is depending on it at all.
 *
 *    Fix: make the price EVENT-driven. On a variant change we immediately write
 *    the truth (from the variant-picker JSON, which every probe showed is always
 *    correct) into Globo's targets. Globo's interval then writes the same value
 *    and becomes a no-op. No polling required for correctness.
 *
 * SURCHARGES: Globo owns option surcharges — we must never clobber them.
 * Measured 2026-08-08 on redford-rx: Globo does NOT fold them into the price
 * element. Selecting "Trivex (+ $50.00)" left [data-gpo-product-unit-price] at
 * $275 and announced the surcharge separately, in .gpo-total: "Selections will
 * add $50.00 to the price". So the unit price legitimately equals the variant
 * price. We still accept variant + that announced surcharge as valid, so this
 * stays correct if a future Globo config does fold surcharges in.
 *
 * Two guard designs were tried and discarded, both worth not repeating:
 * - Inferring the surcharge from the DISPLAYED value. Globo's stale write got
 *   mislearned as a +$120 surcharge and inverted the price on 5 of 7 toggles.
 *   Never derive intent from the pixels the buggy component just wrote.
 * - Scanning GPOConfigs.options for a `price` key. It reported ZERO priced
 *   options on a product that visibly sells +$50 and +$60 lens upgrades, so it
 *   guarded nothing. We have no spec for that config shape; read what Globo
 *   RENDERS instead.
 *
 * Robustness notes (learned 2026-07-11 on-device, still true):
 * - Observe document.body, NOT .product-information — Horizon's morph/section
 *   re-render can replace that node, silently orphaning the observer. The price
 *   element also lives OUTSIDE .product-information on the Rx PDP.
 * - No mo.takeRecords() re-entrancy guard — it also discards legit Globo
 *   mutations queued during the rAF wait. Every write here is idempotent (a
 *   second pass computes the same value and writes nothing), so the feedback
 *   loop self-terminates.
 * - Also run on Horizon's `variant:update`; document is never replaced, so this
 *   survives any morph.
 */
(function () {
  var ZERO_CENTS = /(\d)\.00(?=\D|$)/g;
  var mo = null;
  var scheduled = false;
  var seenTarget = false;

  function targets() {
    return document.querySelectorAll('[data-gpo-product-unit-price]');
  }

  /* ---------- Globo's currently-selected option surcharge, in cents ---------- */
  // Measured 2026-08-08 on redford-rx: Globo does NOT fold option surcharges into
  // [data-gpo-product-unit-price]. Selecting "Trivex (+ $50.00)" left the unit
  // price at $275 and announced the surcharge in its own element instead:
  // "Selections will add $50.00 to the price" (.gpo-total / .gpo-total-text).
  //
  // An earlier guard tried to detect priced options by walking GPOConfigs.options
  // for a `price` key. It found zero on a product that visibly has +$50 and +$60
  // options, so it never actually guarded anything. Read what Globo RENDERS, not
  // a config shape we have no spec for.
  //
  // We therefore treat BOTH the bare variant price and variant + this surcharge
  // as legitimate, and only correct a value that is neither — which is exactly
  // the stale-previous-variant case. That stays correct whether or not a future
  // Globo config starts including surcharges in the unit price.
  function optionSurcharge() {
    var el = document.querySelector('.gpo-total-text, .gpo-total');
    if (!el) return 0;
    var m = el.textContent.match(/\$([\d,]+(?:\.\d{2})?)/);
    if (!m) return 0;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  /* ---------- truth: the variant-picker's own JSON ---------- */
  function currentVariant() {
    var picker = document.querySelector('variant-picker');
    var tag = picker && picker.querySelector('script[type="application/json"]');
    if (!tag) return null;
    try {
      var v = JSON.parse(tag.textContent);
      return v && typeof v.price === 'number' ? v : null;
    } catch (e) {
      return null;
    }
  }

  /* ---------- money ---------- */
  // Theme convention: drop ".00", keep real cents, comma thousands separator.
  function formatMoney(cents) {
    var s = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function readCents(el) {
    if (!el) return null;
    var m = el.textContent.match(/([\d,]+(?:\.\d{2})?)/);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isNaN(n) ? null : Math.round(n * 100);
  }

  /* ---------- writes ---------- */
  function writeInto(el, text) {
    if (!el) return;
    var moneyEls = el.querySelectorAll('.money');
    if (moneyEls.length) {
      moneyEls.forEach(function (m) {
        if (m.textContent !== text) m.textContent = text;
      });
    } else if (el.textContent !== text) {
      el.textContent = text;
    }
  }

  // Job 1: strip Globo's ".00" wherever it wrote one.
  function trimNode(el) {
    if (!el) return;
    var moneyEls = el.querySelectorAll('.money');
    var list = moneyEls.length ? moneyEls : [el];
    Array.prototype.forEach.call(list, function (m) {
      var t = m.textContent;
      var n = t.replace(ZERO_CENTS, '$1');
      if (n !== t) m.textContent = n;
    });
  }

  /* ---------- job 2: enforce the selected variant's price ---------- */
  // Stateless and idempotent: the correct value is always the current variant's
  // price. Globo's stale write triggers a mutation, this runs, and it converges
  // immediately — no settle window and no timer required for correctness.
  function reconcile() {
    var list = targets();
    if (!list.length) return;
    seenTarget = true;

    var v = currentVariant();
    if (!v) return;

    // Both of these are legitimate; anything else is a stale write.
    var bare = v.price;
    var withOptions = v.price + optionSurcharge();
    // Correct TO the surcharge-inclusive value, not the bare one. Writing `bare`
    // here under-displayed the price by the surcharge whenever a correction fired
    // with options selected — e.g. $395 shown where Progressive + Trivex is $445.
    // Under-display is the dangerous direction. When no options are selected
    // withOptions === bare, so this is the same value in the common case.
    var text = formatMoney(withOptions);

    Array.prototype.forEach.call(list, function (el) {
      var shown = readCents(el);
      if (shown === bare || shown === withOptions) return;
      writeInto(el, text);
    });
  }

  function run() {
    var list = targets();
    if (list.length) seenTarget = true;
    reconcile();
    Array.prototype.forEach.call(targets(), trimNode);
  }

  // Run SYNCHRONOUSLY in the observer callback. The earlier version batched via
  // requestAnimationFrame with a setTimeout fallback — but this whole bug exists
  // because Chrome throttles timers in background/hidden frames, and the theme
  // editor preview is exactly such a frame. rAF never fires there and setTimeout
  // is clamped to ~1s, so a timer-batched corrector inherits the very failure it
  // is meant to repair. Measured 2026-08-08 in a background tab: the correction
  // and the ".00" trim both landed ~1s late, leaving Globo's value visible.
  //
  // Every write here is idempotent, so the observer re-entry converges in one
  // extra pass rather than looping. `scheduled` guards against re-entrancy while
  // our own writes are still being applied.
  function runSafe() {
    if (scheduled) return;
    scheduled = true;
    try {
      run();
    } finally {
      scheduled = false;
    }
  }

  run();
  document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('load', run);
  document.addEventListener('variant:update', runSafe);

  if (window.MutationObserver && document.body) {
    mo = new MutationObserver(runSafe);
    mo.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });
    // If this isn't an Rx PDP at all, stop scanning every mutation. But be very
    // conservative about it: this deadline is itself a timer, and in a throttled
    // editor frame Globo can inject its price nodes long after 5s. Disconnecting
    // there would kill the corrector permanently on exactly the surface that
    // needs it. So: re-check at fire time, give it 30s rather than 5s, and never
    // disconnect while any Globo markup is on the page.
    setTimeout(function () {
      if (!mo) return;
      var stillNoTarget = !seenTarget && !targets().length;
      var globoPresent = !!document.querySelector('[class*="gpo-"], .gpo-app, globo-product-options');
      if (stillNoTarget && !globoPresent) {
        mo.disconnect();
        mo = null;
      }
    }, 30000);
  }
})();
